import { IonButton, IonContent, IonPage, IonToggle, useIonToast } from "@ionic/react";
import { useEffect, useState } from "react";

import Header from "src/components/Header";
import Autocompleteoperario from "src/components/Autocompleteoperario";
import LoginAPI from "src/services/login";
import { defaultFeatureSwitches, FeatureSwitches, isDevCargo } from "src/config/featureSwitches";
import { actualizarFeatureSwitches, obtenerFeatureSwitches } from "src/services/featureSwitches";
import {
  actualizarMarcacionBloqueo,
  MarcacionBloqueoHistorialItem,
  MarcacionBloqueoItem,
  obtenerMarcacionesBloqueos,
  obtenerMarcacionesBloqueosHistorial,
} from "src/services/marcacionesBloqueos";

const CEDULA_AUTORIZADA = "5355286";

type OperarioSeleccion = { id: string; usuario: string; cargo: string };

const Settings: React.FC = () => {
  const [present] = useIonToast();
  const [perfil, setPerfil] = useState<any>(null);
  const [perfilLoading, setPerfilLoading] = useState(true);
  const [perfilError, setPerfilError] = useState("");
  const [switches, setSwitches] = useState<FeatureSwitches>(defaultFeatureSwitches);
  const [switchesLoading, setSwitchesLoading] = useState(false);
  const [switchesError, setSwitchesError] = useState("");
  const [operarioSeleccionado, setOperarioSeleccionado] = useState<OperarioSeleccion | null>(null);
  const [bloqueos, setBloqueos] = useState<MarcacionBloqueoItem[]>([]);
  const [historial, setHistorial] = useState<MarcacionBloqueoHistorialItem[]>([]);
  const [bloqueosLoading, setBloqueosLoading] = useState(false);
  const [bloqueosError, setBloqueosError] = useState("");

  useEffect(() => {
    const cargarPerfil = async () => {
      try {
        const response = await LoginAPI.me();
        setPerfil(response?.data?.datos || null);
      } catch {
        setPerfilError("No se pudo cargar el perfil.");
      } finally {
        setPerfilLoading(false);
      }
    };

    cargarPerfil();
  }, []);

  useEffect(() => {
    const cargarSwitches = async () => {
      setSwitchesLoading(true);
      setSwitchesError("");
      try {
        const data = await obtenerFeatureSwitches();
        setSwitches(data);
      } catch {
        setSwitchesError("No se pudieron cargar los switches.");
        setSwitches(defaultFeatureSwitches);
      } finally {
        setSwitchesLoading(false);
      }
    };

    cargarSwitches();
  }, []);

  const puedeEditar = () => {
    const cedula = String(perfil?.emp_cedula || "");
    const cargo = String(perfil?.emp_cargo || "");
    return cedula === CEDULA_AUTORIZADA && isDevCargo(cargo);
  };

  const actualizarSwitches = async (next: FeatureSwitches) => {
    setSwitchesLoading(true);
    setSwitchesError("");
    try {
      const data = await actualizarFeatureSwitches(next);
      setSwitches(data);
      present({ message: "Switches actualizados.", duration: 3000, color: "success" });
    } catch {
      setSwitchesError("No se pudieron guardar los switches.");
      setSwitches(next);
    } finally {
      setSwitchesLoading(false);
    }
  };

  const cargarBloqueos = async () => {
    setBloqueosLoading(true);
    setBloqueosError("");
    try {
      const [listaBloqueos, listaHistorial] = await Promise.all([
        obtenerMarcacionesBloqueos(),
        obtenerMarcacionesBloqueosHistorial(80),
      ]);
      setBloqueos(listaBloqueos);
      setHistorial(listaHistorial);
    } catch {
      setBloqueosError("No se pudieron cargar los bloqueos de marcaciones.");
      setBloqueos([]);
      setHistorial([]);
    } finally {
      setBloqueosLoading(false);
    }
  };

  useEffect(() => {
    if (!puedeEditar()) return;

    const cargarDatosGestion = async () => {
      await cargarBloqueos();
    };

    cargarDatosGestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfil]);

  const bloquearLegajo = async () => {
    const legajo = Number(operarioSeleccionado?.id || 0);
    if (!Number.isFinite(legajo) || legajo <= 0) {
      present({ message: "Selecciona un legajo válido.", duration: 3000, color: "warning" });
      return;
    }

    setBloqueosLoading(true);
    setBloqueosError("");
    try {
      const lista = await actualizarMarcacionBloqueo({
        legajo,
        bloqueado: true,
        motivo: "",
      });
      setBloqueos(lista);
      setOperarioSeleccionado(null);
      await cargarBloqueos();
      present({ message: `Legajo ${legajo} bloqueado para marcaciones.`, duration: 3000, color: "success" });
    } catch {
      setBloqueosError("No se pudo bloquear el legajo.");
    } finally {
      setBloqueosLoading(false);
    }
  };

  const desbloquearLegajo = async (legajo: number) => {
    setBloqueosLoading(true);
    setBloqueosError("");
    try {
      const lista = await actualizarMarcacionBloqueo({
        legajo,
        bloqueado: false,
        motivo: "Desbloqueo manual desde Settings",
      });
      setBloqueos(lista);
      await cargarBloqueos();
      present({ message: `Legajo ${legajo} desbloqueado.`, duration: 3000, color: "success" });
    } catch {
      setBloqueosError("No se pudo desbloquear el legajo.");
    } finally {
      setBloqueosLoading(false);
    }
  };

  if (perfilError) {
    return (
      <IonPage>
        <Header titulo="Settings" />
        <IonContent id="settings">
          <div className="container mt-1">
            <div className="kpi-card">
              <div className="kpi-item-row">{perfilError}</div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (perfilLoading) {
    return (
      <IonPage>
        <Header titulo="Settings" />
        <IonContent id="settings">
          <div className="container mt-1">
            <div className="kpi-card">
              <div className="kpi-item-row">Cargando...</div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!puedeEditar()) {
    return (
      <IonPage>
        <Header titulo="Settings" />
        <IonContent id="settings">
          <div className="container mt-1">
            <div className="kpi-card">
              <div className="kpi-item-row">No autorizado.</div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <Header titulo="Settings" />
      <IonContent id="settings">
        <div className="container mt-1">
          <div className="kpi-grid" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="kpi-card">
              <div className="kpi-title">Switches globales</div>
              {switchesError && <div className="kpi-gestion-error">{switchesError}</div>}
              <div className="kpi-item-row kpi-switch-row">
                <span className="kpi-switch-label">Gestionar SIN-A</span>
                <IonToggle
                  checked={switches.sinaGestionar}
                  disabled={switchesLoading}
                  onIonChange={(e) =>
                    actualizarSwitches({
                      ...switches,
                      sinaGestionar: Boolean(e.detail.checked),
                    })
                  }
                />
              </div>
              <div className="kpi-item-row kpi-switch-row">
                <span className="kpi-switch-label">Notificaciones</span>
                <IonToggle
                  checked={switches.notificaciones}
                  disabled={switchesLoading}
                  onIonChange={(e) =>
                    actualizarSwitches({
                      ...switches,
                      notificaciones: Boolean(e.detail.checked),
                    })
                  }
                />
              </div>
              <div className="kpi-item-row kpi-switch-row">
                <span className="kpi-switch-label">Passkey</span>
                <IonToggle
                  checked={switches.passkey}
                  disabled={switchesLoading}
                  onIonChange={(e) =>
                    actualizarSwitches({
                      ...switches,
                      passkey: Boolean(e.detail.checked),
                    })
                  }
                />
              </div>
              <div className="kpi-item-row kpi-switch-row">
                <span className="kpi-switch-label">Tarjetas de Inicio</span>
                <IonToggle
                  checked={switches.homeKpis}
                  disabled={switchesLoading}
                  onIonChange={(e) =>
                    actualizarSwitches({
                      ...switches,
                      homeKpis: Boolean(e.detail.checked),
                    })
                  }
                />
              </div>
              <div className="kpi-item-row kpi-switch-row">
                <span className="kpi-switch-label">Clima Organizacional</span>
                <IonToggle
                  checked={switches.climaOrganizacional}
                  disabled={switchesLoading}
                  onIonChange={(e) =>
                    actualizarSwitches({
                      ...switches,
                      climaOrganizacional: Boolean(e.detail.checked),
                    })
                  }
                />
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-title">Bloqueo de marcaciones por legajo</div>
              {bloqueosError && <div className="kpi-gestion-error">{bloqueosError}</div>}

              <div className="kpi-item-row" style={{ display: "block" }}>
                <div style={{ fontSize: 12, marginBottom: 4 }}>Legajo</div>
                <Autocompleteoperario
                  className="w-100"
                  label="Buscar por legajo o nombre"
                  value={operarioSeleccionado}
                  setValue={setOperarioSeleccionado}
                  filterCargo={null}
                />
              </div>

              <div className="kpi-item-row">
                <IonButton disabled={bloqueosLoading} onClick={bloquearLegajo}>Bloquear legajo</IonButton>
              </div>

              <div className="kpi-item-row" style={{ display: "block" }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Legajos bloqueados</div>
                {bloqueosLoading && bloqueos.length === 0 ? (
                  <div>Cargando...</div>
                ) : bloqueos.length === 0 ? (
                  <div>No hay legajos bloqueados.</div>
                ) : (
                  bloqueos.map((item) => (
                    <div key={item.legajo} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <div>
                        <div>Legajo {item.legajo}</div>
                        <div style={{ fontSize: 12, opacity: 0.8 }}>{item.motivo || "Sin motivo"}</div>
                      </div>
                      <IonButton color="medium" fill="outline" size="small" disabled={bloqueosLoading} onClick={() => desbloquearLegajo(item.legajo)}>
                        Desbloquear
                      </IonButton>
                    </div>
                  ))
                )}
              </div>

              <div className="kpi-item-row" style={{ display: "block" }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Historial reciente</div>
                {historial.length === 0 ? (
                  <div>Sin movimientos.</div>
                ) : (
                  historial.slice(0, 20).map((item) => (
                    <div key={item.id} style={{ marginBottom: 6, fontSize: 13 }}>
                      {item.cambiado_en} · Legajo {item.legajo} · {item.accion} · {item.cambiado_por}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Settings;
